import { Mail } from "lucide-react";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "../../../components/ui/dialog";
import type { Invoice } from "./types";
import { inr } from "./utils";

interface InvoiceMailSentConfirmModalProps {
  open: boolean;
  invoice: Invoice | null;
  isSubmitting?: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

const InvoiceMailSentConfirmModal = ({
  open,
  invoice,
  isSubmitting = false,
  onOpenChange,
  onConfirm,
}: InvoiceMailSentConfirmModalProps) => {
  if (!invoice) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md gap-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-xl">
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="bg-primary/10 flex h-16 w-16 items-center justify-center rounded-full">
            <Mail className="text-primary h-8 w-8" />
          </div>

          <div className="space-y-2">
            <DialogTitle className="text-lg font-semibold text-[#111827]">
              Mark Invoice as Sent?
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-sm leading-relaxed">
              You are about to mark this invoice as sent. The status will be
              updated and the invoice will be recorded as mailed to the client.
            </DialogDescription>
          </div>

          <div className="bg-background w-full rounded-xl border border-gray-200 p-4 text-left">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="space-y-0.5">
                <p className="text-muted-foreground text-xs">Invoice #</p>
                <p className="font-semibold text-[#111827]">{invoice.id}</p>
              </div>
              <div className="space-y-0.5">
                <p className="text-muted-foreground text-xs">Client</p>
                <p className="font-semibold text-[#111827]">{invoice.client}</p>
              </div>
              <div className="space-y-0.5">
                <p className="text-muted-foreground text-xs">Billing Period</p>
                <p className="text-primary font-medium">{invoice.period}</p>
              </div>
              <div className="space-y-0.5">
                <p className="text-muted-foreground text-xs">Total Amount</p>
                <p className="font-semibold text-[#111827]">
                  {inr(invoice.total)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="flex gap-3 sm:justify-center">
          <Button
            type="button"
            variant="outline"
            disabled={isSubmitting}
            onClick={() => onOpenChange(false)}
            className="rounded-xl border-gray-300 bg-white px-5 hover:bg-gray-50"
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={isSubmitting}
            onClick={onConfirm}
            className="bg-primary hover:bg-primary/90 rounded-xl px-5 text-white"
          >
            {isSubmitting ? "Sending..." : "Yes, Mark as Sent"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InvoiceMailSentConfirmModal;
